import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type NetworkQuality = 'excellent' | 'good' | 'deficient' | 'poor' | 'offline';

export interface NetworkStatus {
    isOnline: boolean;
    quality: NetworkQuality;
    latency: number | null; // ms
}

@Injectable({
    providedIn: 'root'
})
export class NetworkStatusService implements OnDestroy {

    private readonly TEST_INTERVAL = 10000; // 10 segundos después de cada test
    private readonly LATENCY_EXCELLENT = 300; // ms
    private readonly LATENCY_GOOD = 800; // ms
    private readonly LATENCY_DEFICIENT = 1500; // ms

    // URLs de recursos pequeños para ping
    private readonly PING_URLS = [
        'https://www.google.com/favicon.ico',
        'https://www.cloudflare.com/favicon.ico',
        'https://cdn.jsdelivr.net/npm/primeicons@6.0.1/fonts/primeicons.woff2'
    ];

    private destroy$ = new Subject<void>();
    private statusSubject = new BehaviorSubject<NetworkStatus>({
        isOnline: navigator.onLine,
        quality: navigator.onLine ? 'good' : 'offline',
        latency: null
    });

    status$: Observable<NetworkStatus> = this.statusSubject.asObservable();
    private isDestroyed = false;
    private abortController: AbortController | null = null;

    constructor() {
        this.initListeners();
        this.runLatencyTest();
    }

    private initListeners(): void {
        window.addEventListener('online', () => this.handleOnline());
        window.addEventListener('offline', () => this.handleOffline());
    }

    private handleOnline(): void {
        this.runLatencyTest();
    }

    private handleOffline(): void {
        this.cancelPendingRequest();
        this.statusSubject.next({
            isOnline: false,
            quality: 'offline',
            latency: null
        });
    }

    private scheduleNextTest(): void {
        if (this.isDestroyed) {
            return;
        }
        timer(this.TEST_INTERVAL)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (navigator.onLine) {
                    this.runLatencyTest();
                } else {
                    this.handleOffline();
                    this.scheduleNextTest();
                }
            });
    }

    private cancelPendingRequest(): void {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    private async runLatencyTest(): Promise<void> {
        if (!navigator.onLine) {
            this.handleOffline();
            this.scheduleNextTest();
            return;
        }

        this.cancelPendingRequest();
        this.abortController = new AbortController();

        const pingUrl = this.PING_URLS[Math.floor(Math.random() * this.PING_URLS.length)];
        const cacheBuster = `?_=${Date.now()}`;
        const startTime = performance.now();

        try {
            await fetch(pingUrl + cacheBuster, {
                method: 'HEAD',
                mode: 'no-cors',
                cache: 'no-store',
                signal: this.abortController.signal,
                // @ts-ignore - priority es válido pero TypeScript no lo reconoce aún
                priority: 'low' // Prioridad baja para no interferir con otras peticiones
            });

            const latency = Math.round(performance.now() - startTime);
            const quality = this.getQualityFromLatency(latency);

            this.statusSubject.next({
                isOnline: true,
                quality,
                latency
            });
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                return;
            }

            if (navigator.onLine) {
                this.statusSubject.next({
                    isOnline: true,
                    quality: 'poor',
                    latency: null
                });
            } else {
                this.handleOffline();
            }
        } finally {
            this.abortController = null;
            this.scheduleNextTest();
        }
    }

    private getQualityFromLatency(latency: number): NetworkQuality {
        if (latency <= this.LATENCY_EXCELLENT) {
            return 'excellent';
        } else if (latency <= this.LATENCY_GOOD) {
            return 'good';
        } else if (latency <= this.LATENCY_DEFICIENT) {
            return 'deficient';
        }
        return 'poor';
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        this.cancelPendingRequest();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
